import { useMemo, useState } from "react";
import {
  Check, CheckCircle2, Circle, MapPin, ExternalLink, Loader2, ClipboardCheck, Clock,
  Filter, Download, AlertTriangle, X, Users, Tag,
} from "lucide-react";
import { frenchDate, frenchDateShort } from "@/lib/shariti";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// --- Utilitaires date locaux (sans décalage UTC) pour l'alerte de fin de semaine.
function toISODateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function mondayOfISO(iso) {
  const d = new Date(iso + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // 0 = lundi
  d.setDate(d.getDate() - day);
  return toISODateLocal(d);
}

// --- Construit la structure « semaine » du suivi coordination à partir des
// bookings déjà chargés du planning (onglet intégré). Même forme que la réponse
// du endpoint GET /api/planning/coordination (page dédiée) pour un rendu identique.
export function buildCoordinationWeek(bookings, days, lignes) {
  const ligneMap = {};
  (lignes || []).forEach((l) => { ligneMap[l.id] = l; });
  const live = (bookings || []).filter((b) => !b.is_historical && b.status !== "annule");
  const toItem = (b) => ({
    id: b.id,
    spot_id: b.spot_id,
    spot_name: b.spot_name || "(spot inconnu)",
    spot_address: b.spot_address || null,
    ville: b.spot_ville || null,
    date: String(b.date).slice(0, 10),
    status: b.status,
    status_label: b.status_label,
    cdp: b.cdp || { name: null, color: "#F89F63" },
    cdp_id: b.cdp_id || null,
    association: b.association || {},
    ligne_identifiant: (ligneMap[b.ligne_id] || {}).identifiant || null,
    code_campagne: b.code_campagne || null,
    declared_to_coordination: !!b.declared_to_coordination,
    declared_by: b.declared_by || null,
    declared_at: b.declared_at || null,
  });
  const byName = (a, b) => (a.spot_name || "").toLowerCase().localeCompare((b.spot_name || "").toLowerCase());
  const dayGroups = (days || []).map((d) => {
    const iso = String(d.date).slice(0, 10);
    const dayItems = live.filter((b) => String(b.date).slice(0, 10) === iso).map(toItem).sort(byName);
    const declared = dayItems.filter((i) => i.declared_to_coordination);
    const pending = dayItems.filter((i) => !i.declared_to_coordination);
    return {
      date: iso, label: d.label, is_sunday: !!d.is_sunday, total: dayItems.length,
      declared_count: declared.length, pending_count: pending.length, declared, pending,
    };
  });
  const total = dayGroups.reduce((s, d) => s + d.total, 0);
  const declared_total = dayGroups.reduce((s, d) => s + d.declared_count, 0);
  return { days: dayGroups, total, declared_total, pending_total: total - declared_total };
}

function SpotRow({ item, declared, canDeclare, busy, onToggle, onOpenSpot }) {
  return (
    <div
      data-testid={`coord-row-${item.id}`}
      data-declared={declared ? "true" : "false"}
      className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 transition-colors ${
        declared
          ? "border-[rgba(31,157,85,0.35)] bg-[rgba(31,157,85,0.06)]"
          : "border-[var(--border-soft)] bg-white hover:border-[rgba(238,114,46,0.4)]"
      }`}
    >
      <span
        className="h-6 w-1.5 shrink-0 rounded-full"
        style={{ background: item.cdp?.color || "#F89F63" }}
        title={item.cdp?.name ? `Réservé par ${item.cdp.name}` : undefined}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="break-words text-[13px] font-bold text-ink" data-testid={`coord-row-name-${item.id}`}>{item.spot_name}</span>
          {item.association?.acronym && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[9px] font-bold text-muted-ink">
              <span className="h-2 w-2 rounded-full border border-black/10" style={{ background: item.association?.color || "#EE722E" }} />
              {item.association.acronym}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-ink">
          {(item.ville || item.spot_address) && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="break-words">{item.ville || item.spot_address}</span>
            </span>
          )}
          {item.ligne_identifiant && <span className="font-medium text-ink/70">{item.ligne_identifiant}</span>}
          {item.status_label && <span className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold">{item.status_label}</span>}
        </div>
        {declared && item.declared_by && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-[#1F7A44]" data-testid={`coord-row-declared-by-${item.id}`}>
            <CheckCircle2 className="h-3 w-3" /> Déclaré par {item.declared_by}
            {item.declared_at ? ` · ${frenchDateShort(String(item.declared_at).slice(0, 10))}` : ""}
          </div>
        )}
      </div>
      {onOpenSpot && (
        <button
          type="button"
          onClick={() => onOpenSpot(item)}
          data-testid={`coord-open-${item.id}`}
          title="Ouvrir la fiche du spot"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--shariti-orange)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      )}
      {canDeclare && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onToggle(item, !declared)}
          data-testid={`coord-toggle-${item.id}`}
          aria-pressed={declared}
          title={declared ? "Annuler la déclaration à la coordination" : "Marquer comme déclaré à la coordination"}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-60 ${
            declared
              ? "border-[rgba(31,157,85,0.4)] bg-[rgba(31,157,85,0.1)] text-[#1F7A44] hover:bg-[rgba(31,157,85,0.18)] focus-visible:ring-[#1F9D55]"
              : "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white hover:bg-[#E56622] focus-visible:ring-[var(--shariti-orange)]"
          }`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : declared ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
          {declared ? "Déclaré" : "Déclarer"}
        </button>
      )}
      {!canDeclare && (
        <span
          className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${declared ? "text-[#1F9D55]" : "text-muted-ink/40"}`}
          title={declared ? "Déclaré à la coordination" : "Non déclaré"}
          data-testid={`coord-state-${item.id}`}
        >
          {declared ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
        </span>
      )}
    </div>
  );
}

function DayColumn({ day, canDeclare, busyId, onToggle, onOpenSpot }) {
  const pct = day.total > 0 ? Math.round((day.declared_count / day.total) * 100) : 0;
  const allDone = day.total > 0 && day.pending_count === 0;
  return (
    <div
      data-testid={`coord-day-${day.date}`}
      className="flex flex-col overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]"
    >
      <div className={`flex items-center justify-between gap-2 border-b border-[var(--border-soft)] px-3 py-2.5 ${day.is_sunday ? "bg-[rgba(238,114,46,0.05)]" : "bg-[var(--surface-2)]"}`}>
        <div className="min-w-0">
          <div className="text-sm font-extrabold capitalize text-ink">{day.label}</div>
          <div className="text-[11px] font-medium text-muted-ink">{frenchDate(day.date)}</div>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold ${
            day.total === 0
              ? "bg-[var(--surface-2)] text-muted-ink"
              : allDone
              ? "bg-[rgba(31,157,85,0.14)] text-[#1F7A44]"
              : "bg-[rgba(252,219,83,0.35)] text-[#8A6D00]"
          }`}
          data-testid={`coord-day-count-${day.date}`}
        >
          {allDone && <CheckCircle2 className="h-3.5 w-3.5" />}
          {day.declared_count}/{day.total}
        </span>
      </div>

      {day.total > 0 && (
        <div className="h-1 w-full bg-[var(--surface-2)]">
          <div className="h-full bg-[#1F9D55] transition-[width] duration-300" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="flex-1 space-y-3 p-3">
        {day.total === 0 && (
          <div className="grid place-items-center rounded-xl border border-dashed border-[var(--border-soft)] py-6 text-center" data-testid={`coord-day-empty-${day.date}`}>
            <div className="text-[11px] font-medium text-muted-ink">Aucun créneau placé</div>
          </div>
        )}

        {day.pending_count > 0 && (
          <div data-testid={`coord-pending-block-${day.date}`}>
            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide text-[#8A6D00]">
              <Clock className="h-3.5 w-3.5" /> À déclarer ({day.pending_count})
            </div>
            <div className="space-y-1.5">
              {day.pending.map((it) => (
                <SpotRow key={it.id} item={it} declared={false} canDeclare={canDeclare}
                  busy={busyId === it.id} onToggle={onToggle} onOpenSpot={onOpenSpot} />
              ))}
            </div>
          </div>
        )}

        {day.declared_count > 0 && (
          <div data-testid={`coord-declared-block-${day.date}`}>
            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide text-[#1F7A44]">
              <CheckCircle2 className="h-3.5 w-3.5" /> Déclarés ({day.declared_count})
            </div>
            <div className="space-y-1.5">
              {day.declared.map((it) => (
                <SpotRow key={it.id} item={it} declared canDeclare={canDeclare}
                  busy={busyId === it.id} onToggle={onToggle} onOpenSpot={onOpenSpot} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Bouton de filtre générique (CDP / association) avec liste à cases à cocher.
// Défini au niveau module pour préserver l'état d'ouverture du Popover entre les
// sélections (évite le remontage à chaque changement de filtre).
function CoordFilterButton({ testid, icon: Icon, label, options, selected, onToggle }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="secondary" data-testid={testid}
          className={`h-9 border ${selected.size > 0 ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white"}`}>
          <Icon className="mr-1.5 h-4 w-4" />{label}
          {selected.size > 0 && <span className="ml-1.5 rounded-full bg-[var(--shariti-orange)] px-1.5 text-[11px] font-bold text-white">{selected.size}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2">
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-xs font-bold">{label}</span>
          {selected.size > 0 && (
            <button onClick={() => options.forEach((o) => selected.has(o.id || o.key) && onToggle(o.id || o.key))}
              className="text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline" data-testid={`${testid}-clear`}>
              Effacer
            </button>
          )}
        </div>
        <div className="max-h-64 space-y-0.5 overflow-y-auto no-scrollbar">
          {options.length === 0 && <div className="py-3 text-center text-xs text-muted-ink">Aucun élément.</div>}
          {options.map((o) => {
            const key = o.id || o.key;
            const on = selected.has(key);
            return (
              <button key={key} onClick={() => onToggle(key)} data-testid={`${testid}-opt-${key}`}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-[var(--surface-2)]">
                <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white"}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10" style={{ background: o.color }} />
                <span className="flex-1 break-words">{o.name || o.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Tableau de suivi des déclarations à la coordination (jour par jour).
// Réutilisé par l'onglet du Planning ET la page dédiée du menu.
export function CoordinationBoard({ weekData, canDeclare = false, onDeclare, onOpenSpot, loading = false, weekMonday }) {
  const [busyId, setBusyId] = useState(null);
  const [cdpSel, setCdpSel] = useState(() => new Set());     // vide = tous les CDP
  const [assocSel, setAssocSel] = useState(() => new Set()); // vide = toutes les associations

  const wd = weekData || { days: [], total: 0, declared_total: 0, pending_total: 0 };

  // Options de filtres dérivées des créneaux de la semaine (CDP + associations présents).
  const { cdpOptions, assocOptions } = useMemo(() => {
    const cdps = new Map();
    const assocs = new Map();
    (wd.days || []).forEach((d) => {
      [...(d.declared || []), ...(d.pending || [])].forEach((it) => {
        const cid = it.cdp_id || "__none__";
        if (!cdps.has(cid)) cdps.set(cid, { id: cid, name: it.cdp?.name || "Non attribué", color: it.cdp?.color || "#CBD5E1" });
        const ac = it.association?.acronym || "__none__";
        if (!assocs.has(ac)) assocs.set(ac, { key: ac, label: it.association?.acronym || "—", color: it.association?.color || "#EE722E" });
      });
    });
    return {
      cdpOptions: [...cdps.values()].sort((a, b) => a.name.localeCompare(b.name)),
      assocOptions: [...assocs.values()].sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [wd]);

  // Semaine filtrée : on recalcule les listes et compteurs par jour + la synthèse.
  const filteredWeek = useMemo(() => {
    const match = (it) => {
      if (cdpSel.size > 0 && !cdpSel.has(it.cdp_id || "__none__")) return false;
      if (assocSel.size > 0 && !assocSel.has(it.association?.acronym || "__none__")) return false;
      return true;
    };
    const days = (wd.days || []).map((d) => {
      const declared = (d.declared || []).filter(match);
      const pending = (d.pending || []).filter(match);
      return { ...d, declared, pending, declared_count: declared.length, pending_count: pending.length, total: declared.length + pending.length };
    });
    const total = days.reduce((s, d) => s + d.total, 0);
    const declared_total = days.reduce((s, d) => s + d.declared_count, 0);
    return { days, total, declared_total, pending_total: total - declared_total };
  }, [wd, cdpSel, assocSel]);

  // Alerte « fin de semaine » : semaine EN COURS, à partir du jeudi, avec des
  // créneaux encore à déclarer (basée sur les données NON filtrées).
  const endWeekAlert = useMemo(() => {
    if (!weekMonday || (wd.pending_total || 0) <= 0) return null;
    const today = new Date();
    const todayIso = toISODateLocal(today);
    const currentWeekMonday = mondayOfISO(todayIso);
    // Normalise weekMonday to string (YYYY-MM-DD) in case it's a Date object
    const weekMondayStr = typeof weekMonday === 'string' ? weekMonday : toISODateLocal(new Date(weekMonday));
    if (currentWeekMonday !== weekMondayStr) return null; // uniquement la semaine en cours
    const weekdayIdx = (today.getDay() + 6) % 7; // 0 = lundi
    if (weekdayIdx < 3) return null;             // avant jeudi : pas d'alerte
    return { pending: wd.pending_total, weekdayIdx };
  }, [weekMonday, wd.pending_total]);

  const handleToggle = async (item, declared) => {
    if (!onDeclare) return;
    setBusyId(item.id);
    try {
      await onDeclare(item, declared);
    } finally {
      setBusyId(null);
    }
  };

  const toggleCdp = (id) => setCdpSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAssoc = (key) => setAssocSel((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const resetFilters = () => { setCdpSel(new Set()); setAssocSel(new Set()); };
  const filtersActive = cdpSel.size > 0 || assocSel.size > 0;

  // Export CSV (séparateur « ; » + BOM UTF-8 pour Excel FR). Respecte les filtres actifs.
  const exportCsv = () => {
    const header = ["Jour", "Date", "Spot", "Lieu", "Association", "CDP", "Statut", "État", "Déclaré par", "Déclaré le"];
    const rows = [header];
    (filteredWeek.days || []).forEach((day) => {
      const all = [
        ...(day.pending || []).map((it) => ({ it, declared: false })),
        ...(day.declared || []).map((it) => ({ it, declared: true })),
      ];
      all.forEach(({ it, declared }) => {
        rows.push([
          day.label, it.date, it.spot_name || "", it.ville || it.spot_address || "",
          it.association?.acronym || "", it.cdp?.name || "", it.status_label || it.status || "",
          declared ? "Déclaré" : "À déclarer", it.declared_by || "",
          it.declared_at ? String(it.declared_at).slice(0, 10) : "",
        ]);
      });
    });
    const csv = "\ufeff" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suivi-coordination-${weekMonday || "semaine"}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="coord-loading">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-64 animate-pulse rounded-2xl bg-white" />)}
      </div>
    );
  }

  const fw = filteredWeek;
  const pct = fw.total > 0 ? Math.round((fw.declared_total / fw.total) * 100) : 0;
  const visibleDays = (fw.days || []).filter((d) => !d.is_sunday || d.total > 0);
  const hasAnySpot = (wd.total || 0) > 0;

  return (
    <div data-testid="coordination-board">
      {/* Bandeau de synthèse hebdomadaire */}
      <div className="mb-3 overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-[rgba(31,157,85,0.12)] text-[#1F7A44]">
              <ClipboardCheck className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-extrabold text-ink">Déclarations à la coordination</div>
              <div className="text-[11px] text-muted-ink">Suivi des créneaux placés cette semaine — jour par jour.</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center" data-testid="coord-summary-declared">
              <div className="text-xl font-extrabold leading-none text-[#1F7A44]">{fw.declared_total}</div>
              <div className="text-[10px] font-semibold uppercase text-muted-ink">Déclarés</div>
            </div>
            <div className="text-center" data-testid="coord-summary-pending">
              <div className="text-xl font-extrabold leading-none text-[#8A6D00]">{fw.pending_total}</div>
              <div className="text-[10px] font-semibold uppercase text-muted-ink">À déclarer</div>
            </div>
            <div className="text-center" data-testid="coord-summary-total">
              <div className="text-xl font-extrabold leading-none text-ink">{fw.total}</div>
              <div className="text-[10px] font-semibold uppercase text-muted-ink">Total</div>
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--surface-2)]">
            <div className="h-full rounded-full bg-[#1F9D55] transition-[width] duration-300" style={{ width: `${pct}%` }} data-testid="coord-progress-bar" />
          </div>
          <span className="text-xs font-bold text-[#1F7A44]" data-testid="coord-progress-pct">{pct}%</span>
        </div>
        {/* Barre d'outils : filtres + export */}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[var(--border-soft)] pt-3">
          <CoordFilterButton testid="coord-filter-cdp" icon={Users} label="CDP" options={cdpOptions} selected={cdpSel} onToggle={toggleCdp} />
          <CoordFilterButton testid="coord-filter-assoc" icon={Tag} label="Association" options={assocOptions} selected={assocSel} onToggle={toggleAssoc} />
          {filtersActive && (
            <button onClick={resetFilters} data-testid="coord-filter-reset"
              className="inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-ink">
              <X className="h-3.5 w-3.5" /> Réinitialiser
            </button>
          )}
          <div className="ml-auto">
            <Button variant="secondary" onClick={exportCsv} disabled={fw.total === 0} data-testid="coord-export-csv"
              className="h-9 border border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]">
              <Download className="mr-1.5 h-4 w-4" /> Exporter CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Alerte fin de semaine : créneaux encore à déclarer */}
      {endWeekAlert && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.28)] px-4 py-2.5 text-sm font-semibold text-[#8A6D00]" data-testid="coord-endweek-alert">
          <AlertTriangle className="h-4 w-4 shrink-0 text-[#8A6D00]" />
          <span>
            Fin de semaine proche — il reste <span className="font-extrabold">{endWeekAlert.pending}</span> créneau{endWeekAlert.pending > 1 ? "x" : ""} à déclarer à la coordination.
          </span>
        </div>
      )}

      {!hasAnySpot ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="coord-empty">
          <ClipboardCheck className="mb-2 h-8 w-8 text-muted-ink/40" />
          <div className="text-sm font-bold text-ink">Aucun créneau placé cette semaine</div>
          <div className="mt-0.5 text-xs text-muted-ink">Les créneaux du planning apparaîtront ici pour suivi.</div>
        </div>
      ) : fw.total === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="coord-empty-filtered">
          <Filter className="mb-2 h-8 w-8 text-muted-ink/40" />
          <div className="text-sm font-bold text-ink">Aucun créneau ne correspond aux filtres</div>
          <button onClick={resetFilters} className="mt-2 text-xs font-bold text-[var(--shariti-orange)] hover:underline" data-testid="coord-empty-reset">Réinitialiser les filtres</button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="coord-days-grid">
          {visibleDays.map((day) => (
            <DayColumn key={day.date} day={day} canDeclare={canDeclare} busyId={busyId}
              onToggle={handleToggle} onOpenSpot={onOpenSpot} />
          ))}
        </div>
      )}
    </div>
  );
}
